import { resolvePriceComponentsTotal } from '@local/lib';
import { ActionIcon, Button, Popover, Stack, Table, Text, UnstyledButton } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { Layers, Plus, X } from 'lucide-react';
import { useState } from 'react';

import { pluralize } from '../pluralize';
import { CONTRACT_PRICE_COMPONENTS, FIELD_HELP } from '../qualityFormulaMetadata';
import { AnimatedItem } from './AnimatedItem';
import { SpecSectionPanel } from './SpecSectionPanel';
import { TABLE_HEADER_STYLE } from './tableStyles';

const TH_STYLE: React.CSSProperties = { ...TABLE_HEADER_STYLE, padding: '6px 10px' };

// A visible line under every component row — not just relying on Table's own default row
// border — so each component reads as its own ledger line, matching the mockup.
const TD_STYLE: React.CSSProperties = {
  borderBottom: '1px solid var(--mantine-color-default-border)',
  padding: '6px 10px',
  verticalAlign: 'middle',
};

// Renders each component's dollar value inside a bordered field, the same visual language as the
// form's other input fields, even though the field itself is read-only — see this file's own
// `Table.Td` usage for why the value can never be typed into directly. Fixed width (+ right-aligned
// text, like every other currency field in the form) so a longer/shorter component label never
// resizes the value column.
const VALUE_BOX_STYLE: React.CSSProperties = {
  background: 'var(--mkf-toolbar-btn-bg)',
  border: '1px solid var(--mkf-toolbar-btn-border)',
  borderRadius: 4,
  fontVariantNumeric: 'tabular-nums',
  padding: '4px 10px',
  textAlign: 'right',
  width: 100,
};

// Total Basis sits in its own `Table.Tfoot` (not the last `Table.Tbody` row), with extra top
// padding instead of a rule line, so whitespace — not a border — separates it from the line items
// it sums, matching the mockup's total row.
const TOTAL_TD_STYLE: React.CSSProperties = {
  fontWeight: 700,
  padding: '10px 10px 4px',
  verticalAlign: 'middle',
};

interface PriceComponentAddButtonProps {
  onAdd: (key: string) => void;
  remainingKeys: string[];
}

// Preserves PRICE_COMPONENTS's own declaration order (base price first, then surcharges, then
// adjustments) rather than resorting groups alphabetically.
const groupsInCatalogOrder = (keys: string[]) => [
  ...new Set(
    CONTRACT_PRICE_COMPONENTS.filter(component => keys.includes(component.key)).map(component => component.group),
  ),
];

/**
 * "+ Add Component" popover — lists only components not already in the table (an already-selected
 * one is removed via its own row's remove button instead), grouped the same way `PRICE_COMPONENTS`
 * groups them. Hidden entirely once every catalog component has been added.
 */
const PriceComponentAddButton: React.FC<PriceComponentAddButtonProps> = ({ onAdd, remainingKeys }) => {
  if (remainingKeys.length === 0) return null;
  const remaining = CONTRACT_PRICE_COMPONENTS.filter(component => remainingKeys.includes(component.key));

  return (
    <Popover position="bottom-end" shadow="md" withArrow withinPortal={false}>
      <Popover.Target>
        <Button leftSection={<Plus size={14} />} size="xs" variant="outline">
          Add Component
        </Button>
      </Popover.Target>
      <Popover.Dropdown>
        <Stack gap="sm" w={240}>
          {groupsInCatalogOrder(remainingKeys).map(group => (
            <Stack gap={2} key={group}>
              <Text c="dimmed" fw={700} fz={10} lts={0.3} tt="uppercase">
                {group}
              </Text>
              {remaining
                .filter(component => component.group === group)
                .map(component => (
                  <UnstyledButton
                    key={component.key}
                    onClick={() => {
                      onAdd(component.key);
                    }}
                    px={6}
                    py={4}
                    style={{ borderRadius: 4 }}
                  >
                    <Text fz="sm">{component.label}</Text>
                  </UnstyledButton>
                ))}
            </Stack>
          ))}
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
};

interface PriceComponentsTableProps {
  /** See SpecSectionPanel's own doc comment — only passed `true` for Range (see
   * ProductPriceBasisDetails). */
  collapsible?: boolean;
  /** Stretches this panel to fill its sibling column's height (see SpecSectionPanel's own doc
   * comment) instead of sitting at its natural, often much shorter, content height. */
  fillHeight?: boolean;
  onAdd: (key: string) => void;
  onRemove: (key: string) => void;
  selectedKeys: string[];
}

/**
 * Read-only "Price Components" breakdown — each row's dollar value comes from the shared
 * `PRICE_COMPONENTS` catalog (standing in for the live pricing formula engine, see that catalog's
 * own doc comment) and is never editable here; only *which* components apply is. `Total Basis`
 * sums every selected row's signed value (`resolvePriceComponentsTotal`) and is the number
 * `QualityAdjustmentManager` actually scales `percentFactor` against for this spec — see
 * `resolveEffectiveTradePrice`.
 */
export const PriceComponentsTable: React.FC<PriceComponentsTableProps> = ({
  collapsible,
  fillHeight,
  onAdd,
  onRemove,
  selectedKeys,
}) => {
  const [opened, { open, toggle }] = useDisclosure(true);
  // Rows pending their exit animation — still rendered (and still counted in `selectedComponents`,
  // since `selectedKeys` hasn't changed yet) but faded via AnimatedItem's `visible` prop until
  // the transition finishes and `finalizeRemove` actually calls `onRemove`.
  const [removingKeys, setRemovingKeys] = useState<Set<string>>(() => new Set());
  const selectedComponents = CONTRACT_PRICE_COMPONENTS.filter(component => selectedKeys.includes(component.key));
  const remainingKeys = CONTRACT_PRICE_COMPONENTS.filter(component => !selectedKeys.includes(component.key)).map(
    component => component.key,
  );
  const total = resolvePriceComponentsTotal(selectedKeys);

  // Reopens the panel on every add — a component picked while collapsed (Range mode, see
  // SpecSectionPanel's own doc comment) should be visible immediately, not left hidden behind a
  // closed chevron the user has to notice and toggle themselves.
  const handleAdd = (key: string) => {
    onAdd(key);
    open();
  };

  const handleRemove = (key: string) => {
    setRemovingKeys(previous => new Set(previous).add(key));
  };

  const finalizeRemove = (key: string) => {
    onRemove(key);
    setRemovingKeys(previous => {
      const next = new Set(previous);
      next.delete(key);
      return next;
    });
  };

  return (
    <SpecSectionPanel
      action={<PriceComponentAddButton onAdd={handleAdd} remainingKeys={remainingKeys} />}
      collapsedBadge={pluralize(selectedComponents.length, 'component')}
      collapsible={collapsible}
      description="Components used to calculate the basis for price adjustment."
      fillHeight={fillHeight}
      icon={Layers}
      onToggle={collapsible ? toggle : undefined}
      opened={collapsible ? opened : undefined}
      title="Price Components"
      tooltip={FIELD_HELP.priceBasisPriceComponents}
    >
      {selectedComponents.length === 0 ? (
        <Text c="dimmed" fz="xs">
          No components selected yet — add at least one above.
        </Text>
      ) : (
        <Table.ScrollContainer minWidth={320}>
          <Table verticalSpacing={0} withRowBorders={false}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={TH_STYLE}>Component</Table.Th>
                <Table.Th style={{ ...TH_STYLE, width: 120 }}>Value ($)</Table.Th>
                <Table.Th style={{ ...TH_STYLE, width: 36 }} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {selectedComponents.map(component => (
                <AnimatedItem
                  key={component.key}
                  onExited={() => {
                    finalizeRemove(component.key);
                  }}
                  visible={!removingKeys.has(component.key)}
                >
                  {styles => (
                    <Table.Tr style={styles}>
                      <Table.Td style={TD_STYLE}>{component.label}</Table.Td>
                      {/* Boxed like an input for visual consistency with the rest of the form, but the
                          value itself comes from the contract's pricing formula, not entered here —
                          see this component's own doc comment. */}
                      <Table.Td style={TD_STYLE}>
                        <div style={VALUE_BOX_STYLE}>{component.value.toFixed(2)}</div>
                      </Table.Td>
                      <Table.Td style={TD_STYLE}>
                        <ActionIcon
                          aria-label={`Remove ${component.label}`}
                          color="gray"
                          onClick={() => {
                            handleRemove(component.key);
                          }}
                          size="sm"
                          variant="subtle"
                        >
                          <X size={16} />
                        </ActionIcon>
                      </Table.Td>
                    </Table.Tr>
                  )}
                </AnimatedItem>
              ))}
            </Table.Tbody>
            <Table.Tfoot>
              <Table.Tr>
                <Table.Td style={TOTAL_TD_STYLE}>Total Basis</Table.Td>
                <Table.Td style={TOTAL_TD_STYLE}>${total.toFixed(2)}</Table.Td>
                <Table.Td style={TOTAL_TD_STYLE} />
              </Table.Tr>
            </Table.Tfoot>
          </Table>
        </Table.ScrollContainer>
      )}
    </SpecSectionPanel>
  );
};
